import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, Plus, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { AssistantConversation } from "./AssistantConversation";

export interface AssistantSidebarProps {
  open: boolean;
  onClose: () => void;
  /** Client to scope the assistant's tools to. */
  projectId?: string;
  projectName?: string;
  /** Marketplace campaign to scope to. Narrower than projectId when both are set. */
  campaignId?: string;
}

/**
 * Slide-over chrome around a shared <AssistantConversation>.
 *
 * The conversation itself lives in that component so this and the fullscreen
 * page cannot drift apart. This file owns only the panel: backdrop, transition,
 * `inert`, and the header.
 */
export function AssistantSidebar({
  open,
  onClose,
  projectId,
  projectName,
  campaignId,
}: AssistantSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, forceRender] = useState(0);
  const startNewRef = useRef<(() => void) | null>(null);

  const expand = () => {
    // Close first: the fullscreen route mounts its own conversation, and two
    // live at once would double-fetch hydration and could double-fire an
    // approval resume.
    onClose();
    navigate("/asistente");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel. `inert` when closed: pointer-events-none still leaves the
          controls in the tab order, so a keyboard user would otherwise land
          inside an invisible panel. */}
      <aside
        inert={!open}
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-white border-l border-neutral-200 shadow-2xl z-50 flex flex-col transition-all duration-300 ease-out ${
          open
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-full pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-neutral-900">{t("assistant.title")}</h2>
            {projectName && (
              <p className="text-xs text-neutral-400 truncate">{projectName}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={expand}
              title={t("assistant.fullscreen.expand")}
              aria-label={t("assistant.fullscreen.expand")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => startNewRef.current?.()}
              title={t("assistant.newConversation")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              title={t("assistant.close")}
              className="w-7 h-7 rounded-md flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <AssistantConversation
          projectId={projectId}
          campaignId={campaignId}
          onReady={({ startNewConversation }) => {
            startNewRef.current = startNewConversation;
            // One render so the header button becomes live on first mount;
            // the ref alone would not trigger it.
            forceRender((n) => (n === 0 ? 1 : n));
          }}
        />
      </aside>
    </>
  );
}
